import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Header, { HeaderModifier } from 'components/header';
import { useAccounts } from "@ledgerhq/wallet-api-client-react";
import Spinner from 'components/spinner';
import Alert from 'components/alert';
import { postMainAppApiData } from 'services/api';
import LedgerLiveSyncedJobsTable from 'components/table/ledgerlive/syncedJobsTable';
import { Trans, useTranslation } from 'react-i18next'

type AccountsResponse = Array<{platform: string, address: string, id: number}>

function LedgerLiveSyncComponent() {
  const { t } = useTranslation();
  const { name } = useParams();

  const firstTime = window.location.href.includes('welcome');

  const { accounts, loading, error } = useAccounts();

  const [synced, setSynced] = useState(false);
  const [syncedAccounts, setSyncedAccounts] = useState<AccountsResponse>([]);

  const syncAccounts = useCallback(async () => {
    const response = await postMainAppApiData<AccountsResponse, any>('/ledgerlive/accounts.php', accounts);
    setSyncedAccounts(response);
    setSynced(true);
  }, [accounts]);

  useEffect(() => {
    if (!loading && accounts !== null && accounts.length > 0) {
      syncAccounts();
    } else if (!loading && accounts !== null && accounts.length === 0) {
      setSynced(true);
    }
  }, [accounts, loading, syncAccounts]);

  let firstTimeText = undefined;
  if (firstTime) {
    firstTimeText = (
      <div style={{paddingTop: '10px', paddingBottom: '10px'}}>
        <h2>{t('Thank you for trying out CoinTracking!')}</h2>
        <br />
        <Trans t={t}>
          To unlock the full range of features - including automatic wallet imports and advanced reporting - please take a look at our available account plans <a href="/pro.php">here</a>.
        </Trans>
        <br /><br />
        <Trans t={t}>
          If you’re using the free version, you can still manually import your transactions. Just click the <strong>"Open"</strong> button next to your wallet address, then select <strong>"Check now"</strong> to begin the import process.
        </Trans>
      </div>
    );
  }

  return (
    <div className="ct_padding">
      <div className="ct_section _oldView">
        <div className="ct_section-inner">
          <div className="ct_section-main">
            <div className="ct_overviewDetailsHeader">
              <div className="ct_overviewDetailsHeader-main">
                <Header
                  headline={name ? `${name} ${t('Ledger Live Account Overview', { ns: 'overview' })}` : `${t('Ledger Live Account Overview', { ns: 'overview' })}`}
                  desc={t('Your ledger live accounts are being synchronized. You can see all your ledger live accounts here.', { ns: 'overview' })}
                  modifiers={[
                    HeaderModifier.OLD, HeaderModifier.ONLY_TITLE_MARGIN,
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="ct_section _oldView">
        <div className="ct_section-inner">
          <div className="ct_overviewDetailsHeader-fullSide">
            { firstTimeText }
            { loading && <Spinner /> }
            { !!error && (
              <Alert type="error" hasArrow={false} title="Error">
                <>
                  { error?.err?.code === -32601
                    ? t( 'Please open CT in the Ledger Live app', { ns: 'overview' } )
                    : t('Something went wrong', { ns: 'overview' })
                  }
                  <br /><br />
                  <code>{JSON.stringify(error, null, 2)}</code>
                </>
              </Alert>
            )}
            <LedgerLiveSyncedJobsTable isLoading={loading || !synced} isError={!!error} data={syncedAccounts} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default LedgerLiveSyncComponent;
